import Cookies from "js-cookie"
import {
  HEADER_VALUE_COOKIE_KEY,
  HEADER_KEY,
  URL_QUERY_PARAM_KEY,
} from "./consts"
import { initWidget } from "./widget"
import { getRootDomain, sanitise } from "./helpers"
export interface HeaderInjectorOptions {
  /**
   * @deprecated
   * The key used to identify the scope of the header injector.
   * This parameter is no longer used and will be removed in a future version.
   */

  scopeKey: string

  /**
   * The list of API URLs to inject the header into.
   */
  apiUrls: Array<string | undefined | null>

  /**
   * The element to append the widget to.
   */
  appendToEl?: string

  /**
   * Whether the widget should stick to the top of the page.
   */
  isWidgetSticky?: boolean

  /**
   * Default header value
   */
  defaultHeaderValue?: string
}

export class HeaderInjector {
  public readonly isWidgetSticky: boolean
  public readonly isDefaultValue: boolean = false

  private static instance: HeaderInjector | null = null
  private readonly apiUrls: string[]

  private constructor({
    apiUrls,
    isWidgetSticky,
    defaultHeaderValue,
  }: HeaderInjectorOptions) {
    const defautlHeaderValueSanitised = sanitise(
      defaultHeaderValue?.trim() ?? ""
    )
    this.apiUrls = apiUrls.filter((url): url is string => !!url)
    this.isWidgetSticky = isWidgetSticky ?? false

    if (defautlHeaderValueSanitised) {
      this.isDefaultValue = true
      this.headerValue = defautlHeaderValueSanitised
    } else {
      this.handleUrlQueryParam()
    }

    this.patchXhrOpen()
    this.patchXhrSend()
    this.patchFetch()
  }

  public static init(options: HeaderInjectorOptions) {
    if (!HeaderInjector.instance) {
      HeaderInjector.instance = new HeaderInjector(options)
    }

    initWidget(this.getInstance(), options.appendToEl)
    return HeaderInjector.instance
  }

  public static getInstance() {
    if (!HeaderInjector.instance) {
      throw new Error("HeaderInjector has not been initialized yet")
    }
    return HeaderInjector.instance
  }

  public get isDev(): boolean {
    return globalThis.location.hostname === "localhost"
  }

  public get headerValue() {
    return Cookies.get(HEADER_VALUE_COOKIE_KEY) ?? ""
  }

  public set headerValue(value: string) {
    const sanitisedValue = sanitise(value.trim())
    const cookieOpts = this.cookieOptions()
    if (sanitisedValue) {
      Cookies.set(HEADER_VALUE_COOKIE_KEY, sanitisedValue, {
        expires: 365,
        ...cookieOpts,
      })
    } else {
      Cookies.remove(HEADER_VALUE_COOKIE_KEY, cookieOpts)
    }
  }

  private cookieOptions(): Cookies.CookieAttributes {
    if (this.isDev) return { sameSite: "lax" }
    return {
      domain: getRootDomain(globalThis.location.hostname),
      sameSite: "lax",
      secure: true,
    }
  }

  public copyLinkToClipboard() {
    const url = new URL(globalThis.location.href)
    url.searchParams.set(URL_QUERY_PARAM_KEY, this.headerValue)
    navigator.clipboard.writeText(url.toString())
  }

  private handleUrlQueryParam() {
    const url = new URL(globalThis.location.href)
    const headerValue = url.searchParams.get(URL_QUERY_PARAM_KEY)

    if (!headerValue) return

    this.headerValue = sanitise(headerValue.trim())
    url.searchParams.delete(URL_QUERY_PARAM_KEY)
    history.replaceState(null, "", url)
  }

  private patchXhrOpen() {
    const originalOpen = XMLHttpRequest.prototype.open
    // @ts-expect-error idempotency sentinel
    if (originalOpen.__headerInjectorPatched) return
    const apiUrls = this.apiUrls

    const patched = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null
    ) {
      // @ts-expect-error custom property
      this._injectCustomHeader = apiUrls.some((prefix) =>
        url.toString().startsWith(prefix)
      )
      return originalOpen.call(
        this,
        method,
        url,
        async ?? true,
        username,
        password
      )
    } as XMLHttpRequest["open"]
    // @ts-expect-error idempotency sentinel
    patched.__headerInjectorPatched = true
    XMLHttpRequest.prototype.open = patched
  }

  private patchXhrSend() {
    const originalSend = XMLHttpRequest.prototype.send
    // @ts-expect-error idempotency sentinel
    if (originalSend.__headerInjectorPatched) return
    const getHeaderValue = () => this.headerValue

    const patched = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      // @ts-expect-error custom property
      if (this._injectCustomHeader) {
        const headerValue = getHeaderValue()
        if (headerValue) {
          this.setRequestHeader(HEADER_KEY, headerValue.toLowerCase())
        }
      }
      return originalSend.call(this, body)
    } as XMLHttpRequest["send"]
    // @ts-expect-error idempotency sentinel
    patched.__headerInjectorPatched = true
    XMLHttpRequest.prototype.send = patched
  }

  private patchFetch() {
    if (typeof globalThis.fetch !== "function") return
    // @ts-expect-error idempotency sentinel
    if (globalThis.fetch.__headerInjectorPatched) return

    const apiUrls = this.apiUrls
    const getHeaderValue = () => this.headerValue
    const originalFetch = globalThis.fetch.bind(globalThis)

    const resolveUrl = (input: RequestInfo | URL): string => {
      if (typeof input === "string") return input
      if (input instanceof URL) return input.toString()
      return input.url
    }

    const patched: typeof fetch = (input, init) => {
      const url = resolveUrl(input)
      const shouldInject = apiUrls.some((prefix) => url.startsWith(prefix))
      const headerValue = getHeaderValue()

      if (!shouldInject || !headerValue) {
        return originalFetch(input, init)
      }

      const headers = new Headers(
        init?.headers ?? (input instanceof Request ? input.headers : undefined)
      )
      headers.set(HEADER_KEY, headerValue.toLowerCase())

      return originalFetch(input, { ...init, headers })
    }
    // @ts-expect-error idempotency sentinel
    patched.__headerInjectorPatched = true
    globalThis.fetch = patched
  }
}
